import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import BrandVoiceSection from '../BrandVoiceSection'
import DerivedVoiceCard from '../DerivedVoiceCard'
import EmailStylesSection from '../EmailStylesSection'
import BrandOverview from '../BrandOverview'
import RestrictedTab from '../RestrictedTab'
import useVoiceGlance from '../useVoiceGlance'
import useStoredLook from '../useStoredLook'
import { brandSectionFrom, lookHint, lookStatusOf, soundHint, type BrandSection } from '../brandSections'
import { usableDerivation } from '../voiceReview'
import { useWorkspaceStore } from '../../../stores/workspaceStore'
import { useWorkspaceRole } from '../../../utils/useWorkspaceRole'
import { canAccessFeature } from '../../../utils/permissions'
import type { EmailBrandDetectionReview } from '../../../services/api'
import { Section, SectionLayout, SectionNav, SectionPanel, useVisitedSections } from '../../../ds'

/**
 * Brand Tab — how she sounds and how she looks (v6, MAR-384; it was "Voice"). The tab KEY is
 * still `voice`, so every `?tab=voice` link keeps landing here.
 *
 * ## `?section=` — beside `?tab=`, the Limits pattern (MAR-385)
 *
 * Overview · How we sound · How we look, ONE at a time, derived from the URL at the read with
 * no state mirroring it, so a link lands where it says and Back walks sections. Unknown or
 * missing opens the overview. Authority's `setActive` drops `section` on a tab switch.
 *
 * ## A section switch never drops a draft (MAR-386's `SectionPanel`)
 *
 * Each section sits in a ds `SectionPanel`, as on Store and Limits: mounted the first time it is
 * opened, then kept mounted but `hidden`/`inert` while another is open — so an unsaved voice
 * edit or assistant name survives a trip to How we look and back, and each section's reads run
 * once. A workspace switch drops every hidden section in the same render (`useVisitedSections`'
 * reset key).
 *
 * ⚠️ One difference from Store/Limits: each Brand section draws its OWN `SectionLayout`, so the
 * panel wraps the whole frame rather than sitting in one shared main column. The asides are the
 * sections' live state — "Hear it" plays the form's unsaved tone, the email and chat previews
 * render the styling draft — so they cannot be lifted into one frame the tab owns. The nav element is the
 * same in each; a hidden copy is `display:none` + inert, so only the open one is focusable or
 * announced.
 *
 * Kept mounted has two consequences this tab answers for:
 * - **One poller.** The review card polls while it is MOUNTED (hidden too) and reports into the
 *   glance, so the glance polls only while How we sound has never been opened — never both.
 * - **The overview's live look is re-read when it is shown again after a styling save**, not
 *   while hidden (`BrandOverview`'s `active`); a hidden section makes no reads of its own.
 * - Shopify detection runs once per visit — when How we look is FIRST opened — not on every
 *   return to it.
 *
 * ## The hints are the server's, never guessed
 *
 * How we sound's hint is the voice review `state` (this tab's own read, `useVoiceGlance`). How
 * we look's is Shopify detection's verdict — and detection runs only when How we look opens
 * (MAR-334). Until it has run this visit, the STORED observation answers (`useStoredLook`, a
 * GET that runs no detection and writes nothing — MAR-391); `lookStatusOf` is the one rule.
 *
 * ## The card ↔ form wiring (load-bearing)
 *
 * ⚠️ A confirmation or correction in the card above moves the four traits, so the form must
 * not keep showing the pre-confirmation tone — a Save would write it straight back, stamping
 * merchant markers on traits the merchant just agreed to. It is told to RE-SEED those four
 * (`traitsVersion`), never remounted: a remount threw away every unsaved edit below the card,
 * the assistant name included (PR #324 review). The `key` carries only the WORKSPACE, so B
 * never inherits A's form. The other direction: a save in the form moves the effective voice
 * the card shows under "This is what she uses now", so the card re-reads (`reloadToken`).
 *
 * The card REPORTS every read it lands into this tab's glance (`onRead`), so neither act
 * reloads the glance as well — that was a second pair of reads for the same answer.
 *
 * One derive in flight for the whole tab: the card's derive is tracked by the glance
 * (`onDerive` → `track`) and the card is told about the glance's (`deriving`), so the
 * overview's "Read it again" and the card's cannot start two runs.
 *
 * ## Permissions
 *
 * The brand voice writes through the governance API, which a member may call — so How we sound
 * stays open to a member, with the assistant NAME read-only (it writes through the MAR-170
 * owner/admin workspace endpoint). How we look is wholly a workspace writer and is behind
 * `RestrictedTab`, as is the overview's email preview (its routes refuse a member).
 */
const BrandTab = () => {
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspace?.id)
  const { role } = useWorkspaceRole()
  const canWriteWorkspace = canAccessFeature(role, 'account')
  const [searchParams, setSearchParams] = useSearchParams()
  const section = brandSectionFrom(searchParams)

  const open = useCallback((id: string) => {
    // Merge, never replace — the same rule as `?tab=` (a receipt link's context survives).
    const next = new URLSearchParams(searchParams)
    next.set('section', id)
    setSearchParams(next)
  }, [searchParams, setSearchParams])

  const visited = useVisitedSections(section, workspaceId)
  // ⚠️ The card polls whenever it is MOUNTED — hidden included, since its section stays mounted
  // once opened — and reports each read here. So this read polls only while it is not.
  const glance = useVoiceGlance(workspaceId, { poll: !visited.has('sound') })

  /** Detection's verdict this visit, stamped with its workspace and scoped at the read. */
  const [detection, setDetection] = useState<{ workspaceId: string; state: EmailBrandDetectionReview['state'] } | null>(null)
  const lookState = detection && detection.workspaceId === workspaceId ? detection.state : undefined
  // Before detection has run this visit, the stored observation answers (no detection, no write).
  const storedLook = useStoredLook(workspaceId, canWriteWorkspace)
  const lookStatus = lookStatusOf(lookState, storedLook)
  const onDetection = useCallback((state: EmailBrandDetectionReview['state']) => {
    if (workspaceId) setDetection({ workspaceId, state })
  }, [workspaceId])

  /** Bumped when the card confirms or corrects — a counter, so two confirmations re-seed twice. */
  const [voiceVersion, setVoiceVersion] = useState(0)
  /** Bumped when the form saves; the card re-reads its pair. */
  const [formVersion, setFormVersion] = useState(0)
  // No glance reload in either: both fire only on How we sound, where the card's re-read is
  // reported into the glance (`onRead`) — a reload too would be the same pair read twice.
  const onVoiceChanged = useCallback(() => setVoiceVersion((v) => v + 1), [])
  const onVoiceFormSaved = useCallback(() => setFormVersion((v) => v + 1), [])

  const review = glance.snapshot?.review ?? null
  const usable = !!usableDerivation(review?.derived)
  const nav = (
    <SectionNav
      label="Brand sections"
      value={section}
      onChange={open}
      items={[
        { id: 'overview', label: 'Overview' },
        { id: 'sound', label: 'How we sound', hint: soundHint(review?.state, usable) },
        { id: 'look', label: 'How we look', hint: lookHint(lookStatus) },
      ] satisfies { id: BrandSection; label: string; hint?: string }[]}
    />
  )

  // The trait grid is unfolded unless there is a usable reading for the card above to be the
  // control — "Set it myself instead" lands here with it open.
  const reading = (review?.state === 'proposed' || review?.state === 'confirmed') && usable

  return (
    <>
      <SectionPanel id="overview" value={section} visited={visited}>
        <BrandOverview
          nav={nav}
          glance={glance}
          lookStatus={lookStatus}
          open={open}
          canPreview={canWriteWorkspace}
          active={section === 'overview'}
        />
      </SectionPanel>
      <SectionPanel id="sound" value={section} visited={visited}>
        <BrandVoiceSection
          key={workspaceId ?? ''}
          traitsVersion={voiceVersion}
          nav={nav}
          traitsOpen={!reading}
          nameEditable={canWriteWorkspace}
          onSaved={onVoiceFormSaved}
          lead={
            <Section
              title="How we sound"
              description="She reads your storefront and writes down how you sound, for you to correct. What she cannot read off a shop — the words to avoid, the lines you always use, the name she signs off as — you tell her below."
            >
              <DerivedVoiceCard
                onVoiceChanged={onVoiceChanged}
                reloadToken={formVersion}
                onRead={glance.report}
                deriving={glance.deriving}
                onDerive={glance.track}
              />
            </Section>
          }
        />
      </SectionPanel>
      <SectionPanel id="look" value={section} visited={visited}>
        {canWriteWorkspace ? (
          <EmailStylesSection key={workspaceId ?? ''} nav={nav} onDetection={onDetection} />
        ) : (
          <SectionLayout nav={nav}>
            <RestrictedTab>{null}</RestrictedTab>
          </SectionLayout>
        )}
      </SectionPanel>
    </>
  )
}

export default BrandTab
